import { useEffect } from "react";
import { useState } from "@vrf/fr-vrf-core-react";
import {
  ConfirmationDialog,
  useGlobalNotifier,
  useHandleResponseError,
  CustomErrorHandler,
} from "@vrf/fr-vrf-core-react";
import { usePageNavigation } from "../pages/use-page-navigation";
import { AAD_CONFIG } from "./rest-resource-requester";
import {
  isVrfErrorResponseBody,
  VrfErrorResponseBody,
  VrfResponseError,
} from "@vrf/fr-vrf-rest-resource-client/lib";

export const GLOBAL_ERROR: ConfirmationDialog = {
  type: "error",
  showCancelButton: false,
  title: "System Error",
  message: "Something went wrong. Please try again later.",
};

const COMMON_FIELD_ID = "_common_";
const BAD_REQUEST_ERROR = 400;
const UNAUTHORIZED_ERROR = 401;
const INTERNAL_SERVER_ERROR = 500;
const DEFAULT_ERROR_ID = "error";
const DEFAULT_ERROR_MESSAGE = "Unexpected error.";

export const useRestResourceHook = <REQ, RES>(
  requester: (request: REQ) => Promise<RES>,
) => {
  const [request, setRequest] = useState<REQ | undefined>(undefined);
  const [response, setResponse] = useState<RES | undefined>(undefined);
  const { showLoading, setErrorMessage } = useGlobalNotifier();
  const { isModalPage } = usePageNavigation();

  const customErrorHandler: CustomErrorHandler = async (
    error: VrfResponseError,
    body: unknown,
  ) => {
    if (!isVrfErrorResponseBody(body)) return false;
    const res = body as VrfErrorResponseBody<RES>;

    if (
      res.status >= BAD_REQUEST_ERROR &&
      res.status < INTERNAL_SERVER_ERROR &&
      res.status !== UNAUTHORIZED_ERROR
    ) {
      // 4xx ERROR.
      const alert = {
        fieldPaths: [COMMON_FIELD_ID],
        id: res.message?.messageId ?? DEFAULT_ERROR_ID,
        message: res.message?.message ?? DEFAULT_ERROR_MESSAGE,
      };
      if (
        res.message?.details?.length === 0 &&
        (res.message as any)?.body?.length === 0
      ) {
        setErrorMessage([alert], isModalPage());
      } else if (res.message?.details?.length) {
        const errorMessages = res.message.details.map((detail: any) => {
          return {
            fieldPaths: detail.fieldPaths || [COMMON_FIELD_ID],
            id: detail.messageId ?? DEFAULT_ERROR_ID,
            message: detail.message ?? DEFAULT_ERROR_MESSAGE,
          };
        });
        setErrorMessage(errorMessages, isModalPage());
      } else {
        setResponse(res.message.body);
        setErrorMessage([alert], isModalPage());
      }
      return true;
    }
    return false;
  };

  const { handleResponseError } = useHandleResponseError(
    AAD_CONFIG,
    customErrorHandler,
  );

  useEffect(() => {
    if (request == null) return;
    let subscribed: boolean = true;

    const handleRequest = async (request: REQ) => {
      try {
        const res: any = await requester(request);
        if (res && subscribed) {
          setResponse(res);
        }
      } catch (error) {
        console.log(`An error occurred while calling the api: ${error}`);
        handleResponseError(error, isModalPage(), setResponse);
      }
      showLoading(false);
    };
    showLoading(true);
    handleRequest(request);

    return () => {
      subscribed = false;
    };
  }, [request]);

  return [response, (request: REQ) => setRequest(request)] as const;
};
